import { useState, useEffect } from "react";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { BookOpen, Plus, Link2, FileText, Code, BarChart3 } from "lucide-react";

const blockIcons = { markdown: FileText, query: Code, chart: BarChart3 };

export default function NotebooksPage() {
  const [notebooks, setNotebooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/notebooks").then((r) => { setNotebooks(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  if (loading) return <div className="p-6"><Skeleton className="h-48 bg-white/5" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="notebooks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Notebooks</h1>
          <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>Collaborative investigation workspace</p>
        </div>
        <Button className="rounded-lg font-semibold text-sm border-0 text-[#2D3142]" style={{ background: "linear-gradient(135deg, #29B5E8 0%, #1B8FC2 100%)" }} data-testid="new-notebook-button">
          <Plus className="w-4 h-4 mr-2" /> New Notebook
        </Button>
      </div>

      <div className="space-y-3" data-testid="notebooks-list">
        {notebooks.map((nb, i) => (
          <div key={nb.notebook_id} className="sf-card p-5 animate-fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`notebook-${nb.notebook_id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "rgba(124,58,237,0.08)" }}>
                  <BookOpen className="w-4 h-4" style={{ color: "#7C3AED" }} />
                </div>
                <div>
                  <h3 className="text-[15px] font-bold text-[#2D3142]">{nb.title}</h3>
                  <p className="text-[10px] font-mono" style={{ color: "#6B7280" }}>{nb.notebook_id} / {nb.created_by?.split("@")[0]}</p>
                </div>
              </div>
              <span className="text-[10px] font-mono" style={{ color: "#9CA3AF" }}>Updated {nb.updated_at?.slice(0, 10)}</span>
            </div>

            {/* Block previews */}
            <div className="flex flex-wrap gap-2 mb-3">
              {(nb.blocks || []).map((b, bi) => {
                const BIcon = blockIcons[b.type] || FileText;
                return <div key={bi} className="flex items-center gap-1.5 px-2.5 py-1 rounded" style={{ background: "rgba(45,49,66,0.03)" }}>
                  <BIcon className="w-3 h-3" style={{ color: b.type === "markdown" ? "#A78BFA" : b.type === "query" ? "#29B5E8" : "#22C55E" }} />
                  <span className="text-[10px]" style={{ color: "#6B7280" }}>{b.type}</span>
                </div>;
              })}
            </div>

            {/* Linked incidents */}
            {nb.linked_incidents?.length > 0 && (
              <div className="flex items-center gap-2">
                <Link2 className="w-3 h-3" style={{ color: "#6B7280" }} />
                <span className="text-[10px]" style={{ color: "#6B7280" }}>Linked:</span>
                {nb.linked_incidents.map((inc) => (
                  <Badge key={inc} className="text-[8px] font-mono border-0 rounded" style={{ background: "rgba(190,52,85,0.1)", color: "#BE3455" }}>{inc}</Badge>
                ))}
              </div>
            )}
          </div>
        ))}
        {notebooks.length === 0 && <div className="text-center py-12 font-mono text-[12px]" style={{ color: "#9CA3AF" }}>No notebooks yet</div>}
      </div>
    </div>
  );
}
